import { CSSProperties, useCallback, useRef, useState } from 'react'

import html2canvas from 'html2canvas'
import PptxGenJS from 'pptxgenjs'

/**
 * AIペルソナ 営業紹介資料（/proposal4）。
 *
 * **数字は実測値だけを載せる。** 市場統計は出典を作れないため置いていない。
 * 代わりに「仕組みとして何を持っているか」と「デモ環境での実測」を出す。
 * 商談で使う資料なので、後から裏を取れない数字を混ぜると全体が疑われる。
 */

/* ━━━ データ定義 ━━━ */

const PILLARS = [
  {
    tag: '意味',
    icon: '📖',
    name: 'ペルソナカタログ（W2共通）',
    desc: '何を測るか・どう判定するかをW2が定義。サイトごとに作り直さないので、横断で比較できる',
    color: '#10b981'
  },
  {
    tag: '値',
    icon: '📊',
    name: '顧客の測定と割当',
    desc: '10軸45メトリクスで自社の顧客を測り、主ペルソナを1体に決める。変化はすべて履歴に残す',
    color: '#06b6d4'
  },
  {
    tag: '施策',
    icon: '🎯',
    name: 'ターゲットリスト連携',
    desc: 'ペルソナごとの対象者をW2Commerceへ日次で渡す。11の機能で出し分けられる',
    color: '#f59e0b'
  }
]

/** 仕組みとして持っているもの。すべて実装済みの値 */
const CAPABILITY_STATS = [
  { num: '10軸', label: '顧客を測る観点', note: 'LTV・価格感応・購買行動ほか' },
  { num: '45', label: 'メトリクス', note: '購入・行動・端末・時間帯から算出' },
  { num: '32体', label: 'ペルソナカタログ', note: 'W2共通資産。採用して使う' },
  { num: '日次', label: 'ターゲットリスト連携', note: '差分だけをW2Commerceへ送る' }
]

const PAIN_POINTS = [
  {
    problem: 'セグメントは作れる。でも意味がない',
    impact:
      '「30代女性・購入3回以上」で切っても、その人たちが何を大事にしているかは分からない。施策の打ち手に繋がらない',
    color: '#ef4444'
  },
  {
    problem: '効いたのかどうか分からない',
    impact:
      '配信した群の売上は見えても、配信しなくても買った分と区別できない。予算化の根拠にならない',
    color: '#f59e0b'
  },
  {
    problem: '顧客像が固定されてしまう',
    impact:
      '一度分類したら更新されない。「先月はカート放置常連、今月は定着リピーター」という変化を捉えられない',
    color: '#ec4899'
  },
  {
    problem: '分析と施策が分断している',
    impact:
      'BIで分析しても、対象者リストを手作業でCSV出力して配信ツールに入れ直す。運用が続かない',
    color: '#8b5cf6'
  },
  {
    problem: 'サイトごとに定義がばらばら',
    impact:
      '同じ「優良顧客」でもサイトごとに条件が違い、横断で比べられない。ノウハウが溜まらない',
    color: '#3b82f6'
  },
  {
    problem: '「該当0人」と「測れない」が混ざる',
    impact:
      'データが無いだけなのに「該当者なし」と表示され、判断を誤る。静かに間違うので気づけない',
    color: '#06b6d4'
  }
]

/** 2層構造。ここが他社との一番の違い */
const LAYERS = [
  {
    key: 'w2',
    badge: 'W2が持つ',
    icon: '📖',
    title: '意味と測り方',
    color: '#10b981',
    items: [
      'ペルソナの定義（何をもってそう呼ぶか）',
      '判定に使うメトリクスと算出方法',
      'カタログの改版と配布',
      'サイト横断で同じものさしを保つ'
    ],
    example: '「定着リピーター」とは\n直近365日の注文が3回以上、\nかつ購入間隔が安定している人'
  },
  {
    key: 'site',
    badge: 'サイトが持つ',
    icon: '🏪',
    title: '値と施策',
    color: '#06b6d4',
    items: [
      'どのペルソナを採用するか（自社に要るものだけ）',
      '閾値の調整（30日を60日に、など）',
      '実際に誰が該当するか',
      'そのペルソナに何を打つか'
    ],
    example: '「うちは購入間隔が長い商材だから\n365日ではなく540日で見たい」\n→ サイト内だけ変更できる'
  }
]

const AXES = [
  { name: 'LTV', desc: '購入金額・回数・継続期間', color: '#10b981' },
  { name: '購買行動', desc: '購入間隔・まとめ買い・時間帯', color: '#06b6d4' },
  { name: '価格感応', desc: 'セール・クーポン・ポイント利用', color: '#f59e0b' },
  { name: 'エンゲージ', desc: 'ブランド集中度・再訪頻度', color: '#8b5cf6' },
  { name: '購買目的', desc: 'ギフト・お試し・ストック', color: '#ec4899' },
  { name: 'ライフステージ', desc: '会員歴・夜間利用・生活時間', color: '#3b82f6' },
  { name: 'チャネル', desc: 'PC / スマホ の使い分け', color: '#14b8a6' },
  { name: '情報収集', desc: 'セッション数・閲覧カテゴリ数', color: '#a855f7' },
  { name: '心理', desc: '滞在時間・比較検討の深さ', color: '#f43f5e' },
  { name: 'AI理解', desc: 'レコメンド経由の購入比率', color: '#eab308' }
]

/** デモ環境の実測。**本番の実績ではないことを明示する** */
const MEASURED_EXAMPLES = [
  {
    persona: 'レコメンド反応型プレイヤー',
    metric: 'レコメンド経由で買われた比率',
    value: '34.0%',
    site: '7.5%',
    color: '#10b981'
  },
  {
    persona: 'スマホ中心プレイヤー',
    metric: 'スマートフォンからのアクセス比率',
    value: '93.6%',
    site: '52.8%',
    color: '#06b6d4'
  },
  {
    persona: 'ブランドロイヤルファン',
    metric: '最も多く買ったブランドの購入比率',
    value: '83.5%',
    site: '49.4%',
    color: '#8b5cf6'
  },
  {
    persona: '定着リピータープレイヤー',
    metric: '期間内の購入率',
    value: '86.0%',
    site: '57.9%',
    color: '#f59e0b'
  }
]

const MATCH_VS_ASSIGN = [
  {
    icon: '🔗',
    title: '該当（多対多）',
    desc: '1人が複数のペルソナに当てはまってよい。「ギフト購入者」であり「スマホ中心」でもある、は両方とも事実',
    color: '#06b6d4',
    note: '実測で1人あたり平均3.31体に該当'
  },
  {
    icon: '🎯',
    title: '割当（排他）',
    desc: '効果を測るときは1人1体に決める。複数のリストに入ると配信が重なり、どれが効いたか分からなくなる',
    color: '#10b981',
    note: '①条件の具体性 ②合致度 ③優先度 ④uplift実績 の順で決定'
  }
]

const TRANSITION_POINTS = [
  {
    icon: '📅',
    title: '上書きしない',
    desc: '主ペルソナが変わったら、前の行を閉じて新しい行を足す。「いつからいつまで、どのペルソナだったか」が全部残る',
    color: '#10b981'
  },
  {
    icon: '🔄',
    title: '変化そのものが価値',
    desc: '「2回目未到達 → 定着リピーター」への移動が何人いたかが分かる。育成が効いているかの判断材料になる',
    color: '#06b6d4'
  },
  {
    icon: '🔒',
    title: '測定中は凍結する',
    desc: '効果測定の途中で群の構成が変わると数字が読めなくなる。測定期間中は主ペルソナを固定する',
    color: '#f59e0b'
  }
]

/** ペルソナで出し分けられるW2Commerceの機能。定義書335表を走査した結果 */
const W2_FUNCTIONS = [
  { name: 'CRMメール配信', ready: true },
  { name: 'ランク付与ルール', ready: true },
  { name: 'ポイント倍率スケジュール', ready: true },
  { name: 'クーポン配布スケジュール', ready: true },
  { name: 'セットプロモーション', ready: true },
  { name: '特集ページ', ready: true },
  { name: '商品同梱', ready: false },
  { name: '特集エリアバナー', ready: false },
  { name: 'パーツ管理', ready: false },
  { name: 'ページ管理', ready: false },
  { name: 'ポップアップ', ready: false }
]

const UPLIFT_STEPS = [
  {
    num: '1',
    title: '対照群を切る',
    desc: '割当顧客のうち一定比率を、W2へ渡すリストに載せない。その人たちにはペルソナ由来の施策が当たらない',
    color: '#10b981'
  },
  {
    num: '2',
    title: '施策の実行を観測する',
    desc: 'いつ・どのペルソナに・何が打たれたかを記録する。「リストを渡した日」ではなく「実際に打たれた日」から測る',
    color: '#06b6d4'
  },
  {
    num: '3',
    title: '差を取る',
    desc: '実行日からの購入率と客単価を、配信群と対照群で比べる。差が「ペルソナ配信による上乗せ分」',
    color: '#f59e0b'
  },
  {
    num: '4',
    title: '順位づけに返す',
    desc: '実測できた効果を、次の主ペルソナ判定に使う。狙う価値のあるペルソナが自動的に優先される',
    color: '#8b5cf6'
  }
]

const HONESTY = [
  {
    icon: '⚖️',
    title: '「測れない」と「効果ゼロ」を分ける',
    desc: '対照群が小さい・施策が打たれていない・差が偶然と区別できない。どれも「効果なし」ではありません。値を出さず理由を出します',
    color: '#10b981'
  },
  {
    icon: '🔍',
    title: '信頼できる測定だけを判定に使う',
    desc: '差が弱いものは参考値として表示するだけで、主ペルソナの決定には使いません。根拠の無い値で顧客の分類が動かないようにしています',
    color: '#06b6d4'
  },
  {
    icon: '📝',
    title: '変更はすべて履歴に残す',
    desc: '閾値を変えた・比率を変えた・順位づけに使うようにした。いつ誰が何を変えたかが残るので、数字が動いた理由を後から追えます',
    color: '#f59e0b'
  }
]

const INSIGHT_QUESTIONS = [
  { q: '偏り', desc: '顧客がどのペルソナに寄っているか。寄りすぎ・散りすぎはどこか', color: '#10b981' },
  { q: '動き', desc: '期間内にどこからどこへ移ったか。増えているのは何か', color: '#06b6d4' },
  {
    q: '気づき',
    desc: '全体で見ていたら気づけなかったことは何か（最も重要）',
    color: '#f59e0b'
  },
  { q: '運用', desc: 'どのペルソナを有効化・無効化すべきか。優先度をどう置くか', color: '#8b5cf6' }
]

const BEFORE_AFTER = [
  {
    theme: '顧客の捉え方',
    before: '属性と購入回数で機械的に区切る',
    after: '10軸45メトリクスで「何を大事にしているか」まで見る'
  },
  {
    theme: '定義の管理',
    before: 'サイトごとに条件がばらばら。横断で比べられない',
    after: 'W2共通のカタログ。閾値だけサイトが調整する'
  },
  {
    theme: '変化の把握',
    before: '最新の分類だけ。前がどうだったか分からない',
    after: '推移が全部残る。育成が効いたかを人数で見られる'
  },
  {
    theme: '施策への接続',
    before: 'CSVを手で出して配信ツールへ入れ直す',
    after: 'ターゲットリストを日次で自動連携。11機能で出し分け'
  },
  {
    theme: '効果の測定',
    before: '配信群の売上だけ。打たなくても買った分と区別できない',
    after: '対照群との差で「上乗せ分」を実測する'
  },
  {
    theme: '次の一手',
    before: '担当者の勘と経験に依存',
    after: 'AIが全体との違いから示唆を出す。10世代分を履歴で比較'
  }
]

const STEPS = [
  {
    num: '01',
    title: 'W2Commerceとの連携',
    desc: '会員・受注・商品などのマスタをFTPで受け取ります。既存のSmart Hub連携があればそのまま使えます',
    color: '#10b981'
  },
  {
    num: '02',
    title: 'タグの設置',
    desc: '行動ログ用のタグを全ページに設置します。閲覧・回遊・端末・流入元を取得します',
    color: '#06b6d4'
  },
  {
    num: '03',
    title: 'ペルソナの採用',
    desc: 'カタログ32体から自社に要るものを選びます。条件式と該当人数を見ながら決められます',
    color: '#f59e0b'
  },
  {
    num: '04',
    title: '閾値の調整',
    desc: '商材に合わせて期間や金額を変えます。変更は履歴に残り、サイト内だけに効きます',
    color: '#8b5cf6'
  },
  {
    num: '05',
    title: '有効化とリスト連携',
    desc: '有効にしたペルソナのターゲットリストが日次でW2Commerceへ渡ります',
    color: '#ec4899'
  },
  {
    num: '06',
    title: '効果測定の開始',
    desc: '対照群の比率と測定期間を決めます。施策が打たれた日から自動で測り始めます',
    color: '#3b82f6'
  }
]

const ROADMAP = [
  {
    icon: '🧭',
    title: 'ページ意味層',
    desc: 'W2Commerceのページ管理を取り込み、「購入前にFAQを見る層」「ガイドを読み込む層」といった買い方の性格を測れるようにします',
    color: '#10b981'
  },
  {
    icon: '🛍️',
    title: '買われ方の分析',
    desc: '購入商品の8軸（素材・用途・対象・訴求など）から、ペルソナごとに何が好まれるかを出します',
    color: '#06b6d4'
  },
  {
    icon: '🔔',
    title: '出し分け先の拡張',
    desc: 'ポップアップ・パーツ管理・特集エリアバナーへの連携。ページ内の要素単位でペルソナ別に出し分けられるようにします',
    color: '#f59e0b'
  }
]

const SLIDES = [
  { label: '課題', msg: 'セグメントは作れる。でも「効いたか」が分からない' },
  { label: '仕組み', msg: '意味はW2が持ち、値と施策はサイトが持つ' },
  { label: '顧客を測る', msg: '10軸45メトリクスで見る' },
  { label: '該当と割当', msg: '重なりは残し、測るときだけ1体に決める' },
  { label: '推移', msg: '顧客の変化がすべて履歴に残る' },
  { label: '施策へ繋ぐ', msg: 'ターゲットリスト日次連携と11の出し分け先' },
  { label: '効果測定', msg: '対照群との差で「上乗せ分」を実測する' },
  { label: '誠実さの設計', msg: '測れないことを、測れないと言う' },
  { label: 'AI状況示唆', msg: '分けたから見えたことを、AIが言葉にする' },
  { label: '導入効果', msg: 'Before / After' },
  { label: '導入ステップ', msg: '6ステップで運用に乗せる' },
  { label: 'ロードマップ', msg: '開発中の次の一手' }
]

/* ━━━ 本体 ━━━ */

export default function AIPersonaProposal() {
  const [activeSlide, setActiveSlide] = useState(0)
  const [exporting, setExporting] = useState(false)
  const pageRef = useRef<HTMLDivElement>(null)

  // PPT保存: 全ページを画像としてキャプチャしPPTに出力
  const handleExportPPT = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      const pptx = new PptxGenJS()
      pptx.layout = 'LAYOUT_16x9'
      pptx.author = 'W2 Smart Hub'
      pptx.title = 'AI Persona Service Guide'

      const savedSlide = activeSlide
      const container = pageRef.current
      if (!container) return

      for (let i = -1; i < SLIDES.length; i++) {
        if (i >= 0) {
          setActiveSlide(i)
          await new Promise((r) => setTimeout(r, 300))
        }

        const canvas = await html2canvas(container, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          logging: false
        })

        const slide = pptx.addSlide()
        slide.background = { color: '0f172a' }
        slide.addImage({
          data: canvas.toDataURL('image/png'),
          x: 0,
          y: 0,
          w: 10,
          h: 5.625,
          sizing: { type: 'contain', w: 10, h: 5.625 }
        })

        if (i === -1) {
          setActiveSlide(0)
          await new Promise((r) => setTimeout(r, 300))
        }
      }

      setActiveSlide(savedSlide)
      await pptx.writeFile({ fileName: 'AIPersona_ServiceGuide.pptx' })
    } catch (err) {
      console.error('PPT export error:', err)
      alert('PPT出力に失敗しました: ' + (err as Error).message)
    } finally {
      setExporting(false)
    }
  }, [activeSlide, exporting])

  const tone = (c: string, alpha: string) => `${c}${alpha}`

  return (
    <div style={s.page} ref={pageRef}>
      {/* ── PPT保存ボタン ── */}
      <div style={s.exportBar}>
        <button
          onClick={handleExportPPT}
          disabled={exporting}
          style={{ ...s.exportBtn, opacity: exporting ? 0.5 : 1 }}
        >
          {exporting ? '⏳ PPT出力中...' : '📥 PPTに保存'}
        </button>
      </div>

      {/* ── Hero ── */}
      <section style={s.hero}>
        <div style={s.heroBadge}>AI PERSONA</div>
        <h1 style={s.heroTitle}>
          <span style={s.heroAccent}>AIペルソナ</span>
          <span style={s.heroTitleSub}>Customer Understanding &amp; Measurement</span>
        </h1>
        <p style={s.heroSub}>powered by W2 Smart Hub</p>
        <p style={s.heroDesc}>
          顧客を<strong style={{ color: 'rgba(255,255,255,0.9)' }}>「意味のある単位」に分け</strong>、
          施策につなぎ、<strong style={{ color: 'rgba(255,255,255,0.9)' }}>効いたかどうかを実測</strong>します。
          <br />
          分けて終わりにしない。測り続けることで、狙う価値のある顧客が見えてきます。
        </p>

        <div style={s.moduleRow}>
          {PILLARS.map((m, i) => (
            <div key={i} style={{ ...s.moduleCard, borderColor: tone(m.color, '44') }}>
              <div style={s.moduleHead}>
                <span style={{ fontSize: 20 }}>{m.icon}</span>
                <span
                  style={{ ...s.moduleTag, background: tone(m.color, '22'), color: m.color }}
                >
                  {m.tag}
                </span>
              </div>
              <div style={{ ...s.moduleName, color: m.color }}>{m.name}</div>
              <div style={s.moduleDesc}>{m.desc}</div>
            </div>
          ))}
        </div>

        <div style={s.statGrid}>
          {CAPABILITY_STATS.map((st, i) => (
            <div key={i} style={s.statCard}>
              <div style={s.statNum}>{st.num}</div>
              <div style={s.statLabel}>{st.label}</div>
              <div style={s.statSrc}>{st.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ページ目次 ── */}
      <section style={s.agendaSection}>
        <h2 style={s.agendaSectionTitle}>AIペルソナ サービス紹介</h2>
        <p style={s.agendaHint}>クリックで各セクションに切り替え</p>
        <div style={s.agendaGrid}>
          {SLIDES.map((sl, i) => {
            const isActive = activeSlide === i
            return (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                style={{ ...s.agendaCard, ...(isActive ? s.agendaCardActive : {}) }}
              >
                <div style={{ ...s.agendaNum, ...(isActive ? s.agendaNumActive : {}) }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div style={s.agendaBody}>
                  <div style={{ ...s.agendaLabel, ...(isActive ? s.agendaLabelActive : {}) }}>
                    {sl.label}
                  </div>
                  <div style={{ ...s.agendaMsg, ...(isActive ? s.agendaMsgActive : {}) }}>
                    {sl.msg}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* ── スライド表示 ── */}
      <div style={s.slideContainer}>
        <div style={s.sectionDivider} />
        <div style={s.sectionHeader}>
          <div style={s.sectionNum}>{String(activeSlide + 1).padStart(2, '0')}</div>
          <div>
            <h2 style={s.sectionTitle}>{SLIDES[activeSlide].label}</h2>
            <p style={s.sectionSub}>{SLIDES[activeSlide].msg}</p>
          </div>
        </div>

        {/* ─ 01 課題 ─ */}
        {activeSlide === 0 && (
          <div>
            <h3 style={s.subHeading}>顧客を分けること自体は、もう難しくありません</h3>
            <p style={s.subDesc}>
              難しいのはその先です。分けた集団に意味があるのか、施策を打って効いたのか、
              顧客は今も同じ集団にいるのか。ここが埋まらないと、分析は運用に乗りません。
            </p>

            <div style={s.painGrid}>
              {PAIN_POINTS.map((p, i) => (
                <div key={i} style={s.painCard}>
                  <div style={{ ...s.painDot, background: p.color }} />
                  <div>
                    <div style={s.painProblem}>{p.problem}</div>
                    <div style={s.painImpact}>{p.impact}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...s.alertBanner, marginTop: 24 }}>
              <span style={{ fontSize: 20 }}>💡</span>
              <p style={s.alertText}>
                セグメント機能を入れても、
                <strong style={{ color: '#fbbf24' }}>「意味づけ」「効果測定」「変化の記録」</strong>
                が欠けたままでは取りこぼしは残ります。
                AIペルソナはこの3つを最初から仕組みに入れています。
              </p>
            </div>
          </div>
        )}

        {/* ─ 02 仕組み ─ */}
        {activeSlide === 1 && (
          <div>
            <h3 style={s.subHeading}>意味はW2が持ち、値と施策はサイトが持つ</h3>
            <p style={s.subDesc}>
              ペルソナの定義をサイトごとに作ると、同じ「優良顧客」でも中身が違い、横断で比べられません。
              かといってW2が全部決めると、商材ごとの事情に合いません。
              <strong style={{ color: 'rgba(255,255,255,0.85)' }}>
                そこで「何を測るか」と「いくつで区切るか」を分けました。
              </strong>
            </p>

            <div style={s.scopeGrid}>
              {LAYERS.map((sc, i) => (
                <div
                  key={i}
                  style={{
                    ...s.scopeCard,
                    borderColor: tone(sc.color, '44'),
                    background: sc.key === 'w2' ? 'rgba(16,185,129,0.07)' : 'rgba(6,182,212,0.06)'
                  }}
                >
                  <div style={s.scopeHead}>
                    <span style={{ fontSize: 24 }}>{sc.icon}</span>
                    <span
                      style={{ ...s.scopeBadge, background: tone(sc.color, '22'), color: sc.color }}
                    >
                      {sc.badge}
                    </span>
                  </div>
                  <div style={{ ...s.scopeTitle, color: sc.color }}>{sc.title}</div>
                  <ul style={s.scopeList}>
                    {sc.items.map((it, j) => (
                      <li key={j} style={s.scopeItem}>
                        <span style={{ color: sc.color, flexShrink: 0 }}>▸</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={s.scopeExample}>{sc.example}</div>
                </div>
              ))}
            </div>

            <div style={{ ...s.alertBanner, marginTop: 20 }}>
              <span style={{ fontSize: 20 }}>🔐</span>
              <p style={s.alertText}>
                サイトが閾値を変えても
                <strong style={{ color: '#fbbf24' }}>そのサイトの中だけ</strong>
                に効きます。W2共通のカタログは変わりません。変更はすべて履歴に残ります。
              </p>
            </div>
          </div>
        )}

        {/* ─ 03 顧客を測る ─ */}
        {activeSlide === 2 && (
          <div>
            <h3 style={s.subHeading}>10の観点、45のメトリクス</h3>
            <p style={s.subDesc}>
              受注・会員・行動ログから機械的に算出します。担当者が手で分類することはありません。
            </p>

            <div style={s.axisGrid}>
              {AXES.map((a, i) => (
                <div key={i} style={{ ...s.axisCard, borderColor: tone(a.color, '33') }}>
                  <div style={{ ...s.axisName, color: a.color }}>{a.name}</div>
                  <div style={s.axisDesc}>{a.desc}</div>
                </div>
              ))}
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 28 }}>
              サイト平均と比べて初めて「分けた意味」が出ます
            </h3>
            <p style={s.subDesc}>
              ペルソナ単体の数字だけでは、それが特徴なのか普通なのか判断できません。
              必ずサイト全体の平均とセットで見ます。
            </p>

            <div style={s.measuredGrid}>
              {MEASURED_EXAMPLES.map((m, i) => (
                <div key={i} style={{ ...s.measuredCard, borderColor: tone(m.color, '33') }}>
                  <div style={{ ...s.measuredPersona, color: m.color }}>{m.persona}</div>
                  <div style={s.measuredMetric}>{m.metric}</div>
                  <div style={s.measuredRow}>
                    <div>
                      <div style={{ ...s.measuredValue, color: m.color }}>{m.value}</div>
                      <div style={s.measuredCaption}>このペルソナ</div>
                    </div>
                    <div style={s.measuredArrow}>←</div>
                    <div>
                      <div style={s.measuredSite}>{m.site}</div>
                      <div style={s.measuredCaption}>サイト全体</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p style={s.footnote}>
              ※ デモ環境（会員7,434名・受注22,680件）での実測値です。導入先の実績ではありません。
            </p>
          </div>
        )}

        {/* ─ 04 該当と割当 ─ */}
        {activeSlide === 3 && (
          <div>
            <h3 style={s.subHeading}>重なりは情報。捨てずに残します</h3>
            <p style={s.subDesc}>
              1人が複数のペルソナに当てはまるのは正常です。ただし効果を測るときだけは、
              1人1体に決める必要があります。この2つを分けて持っています。
            </p>

            <div style={s.dualGrid}>
              {MATCH_VS_ASSIGN.map((m, i) => (
                <div key={i} style={{ ...s.dualCard, borderColor: tone(m.color, '44') }}>
                  <div style={s.dualHead}>
                    <span style={{ fontSize: 24 }}>{m.icon}</span>
                    <span style={{ ...s.dualTitle, color: m.color }}>{m.title}</span>
                  </div>
                  <p style={s.dualDesc}>{m.desc}</p>
                  <div style={{ ...s.dualNote, borderColor: tone(m.color, '33') }}>{m.note}</div>
                </div>
              ))}
            </div>

            <div style={{ ...s.alertBanner, marginTop: 20 }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <p style={s.alertText}>
                1人が複数のリストに入ると、
                <strong style={{ color: '#fbbf24' }}>配信が重なってどれが効いたか分からなくなります。</strong>
                だから配信するのは「割当」の方だけです。
              </p>
            </div>
          </div>
        )}

        {/* ─ 05 推移 ─ */}
        {activeSlide === 4 && (
          <div>
            <h3 style={s.subHeading}>顧客は変わります。その変化を残します</h3>
            <p style={s.subDesc}>
              「先月は2回目未到達、今月は定着リピーター」という移動そのものが、施策が効いたかの答えです。
              上書きしてしまうと永久に取り戻せません。
            </p>

            <div style={s.trendGrid}>
              {TRANSITION_POINTS.map((t, i) => (
                <div key={i} style={s.trendCard}>
                  <div style={{ ...s.trendIcon, background: tone(t.color, '22'), color: t.color }}>
                    {t.icon}
                  </div>
                  <div>
                    <div style={{ ...s.trendTitle, color: t.color }}>{t.title}</div>
                    <div style={s.trendDesc}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...s.exampleBox, marginTop: 24 }}>
              <div style={s.exampleTitle}>画面で見えること</div>
              <div style={s.exampleBody}>
                期間内にどのペルソナからどのペルソナへ何人動いたか／新たに割り当たった人数／
                どの条件にも当たらなくなった人数／ペルソナごとの構成比の推移
              </div>
            </div>
          </div>
        )}

        {/* ─ 06 施策へ繋ぐ ─ */}
        {activeSlide === 5 && (
          <div>
            <h3 style={s.subHeading}>分析で終わらせず、W2Commerceへ自動で渡します</h3>
            <p style={s.subDesc}>
              有効にしたペルソナごとにターゲットリストを1本持ち、日次で差分だけを同期します。
              CSVを手で出す運用は要りません。
            </p>

            <div style={s.flowRow}>
              <div style={s.flowStep}>
                <div style={{ ...s.flowIcon, background: 'rgba(16,185,129,0.15)' }}>👥</div>
                <div style={s.flowLabel}>ペルソナ割当</div>
              </div>
              <div style={s.flowArrow}>→</div>
              <div style={s.flowStep}>
                <div style={{ ...s.flowIcon, background: 'rgba(6,182,212,0.15)' }}>📋</div>
                <div style={s.flowLabel}>ターゲットリスト</div>
              </div>
              <div style={s.flowArrow}>→</div>
              <div style={s.flowStep}>
                <div style={{ ...s.flowIcon, background: 'rgba(245,158,11,0.15)' }}>🏪</div>
                <div style={s.flowLabel}>W2Commerce</div>
              </div>
              <div style={s.flowArrow}>→</div>
              <div style={s.flowStep}>
                <div style={{ ...s.flowIcon, background: 'rgba(139,92,246,0.15)' }}>🎯</div>
                <div style={s.flowLabel}>施策の出し分け</div>
              </div>
            </div>

            <h3 style={{ ...s.subHeading, marginTop: 28 }}>
              ペルソナで出し分けられる機能は11あります
            </h3>
            <div style={s.funcGrid}>
              {W2_FUNCTIONS.map((f, i) => (
                <div
                  key={i}
                  style={{
                    ...s.funcCard,
                    borderColor: f.ready ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'
                  }}
                >
                  <span style={{ color: f.ready ? '#10b981' : 'rgba(255,255,255,0.3)' }}>
                    {f.ready ? '✓' : '○'}
                  </span>
                  <span style={s.funcName}>{f.name}</span>
                </div>
              ))}
            </div>
            <p style={s.footnote}>
              ✓ = 現在ご利用いただけるもの ／ ○ = 順次対応予定。W2CommerceのDB定義335表を走査して確認した結果です。
            </p>
          </div>
        )}

        {/* ─ 07 効果測定 ─ */}
        {activeSlide === 6 && (
          <div>
            <h3 style={s.subHeading}>「配信した群が買った」では、効果になりません</h3>
            <p style={s.subDesc}>
              配信しなくても買った人が含まれているためです。同じ条件の人を一部だけ意図的に外し、
              その差を見ることで初めて「上乗せ分」が分かります。
            </p>

            <div style={s.workflowList}>
              {UPLIFT_STEPS.map((ws, i) => (
                <div key={i} style={s.workflowItem}>
                  <div style={s.workflowLeft}>
                    <div style={{ ...s.workflowNum, background: ws.color }}>{ws.num}</div>
                    {i < UPLIFT_STEPS.length - 1 && (
                      <div
                        style={{
                          ...s.workflowLine,
                          background: `linear-gradient(180deg, ${ws.color}, ${UPLIFT_STEPS[i + 1].color})`
                        }}
                      />
                    )}
                  </div>
                  <div style={{ ...s.workflowContent, borderColor: tone(ws.color, '33') }}>
                    <span style={{ ...s.workflowTitle, color: ws.color }}>{ws.title}</span>
                    <p style={s.workflowDesc}>{ws.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...s.alertBanner, marginTop: 20 }}>
              <span style={{ fontSize: 20 }}>🎛️</span>
              <p style={s.alertText}>
                対照群の比率と測定期間は
                <strong style={{ color: '#fbbf24' }}>サイトごとに設定できます</strong>
                （既定10%・30日）。設定を変えると過去の測定が読めなくなるため、変更履歴を残しています。
              </p>
            </div>
          </div>
        )}

        {/* ─ 08 誠実さの設計 ─ */}
        {activeSlide === 7 && (
          <div>
            <h3 style={s.subHeading}>数字が出ることより、数字が正しいことを優先しています</h3>
            <p style={s.subDesc}>
              分析ツールの事故は「エラーにならない」のが厄介です。それらしい数字が出てしまうため、
              間違いに気づけません。AIペルソナは、分からないときに分からないと言う設計にしています。
            </p>

            <div style={s.trendGrid}>
              {HONESTY.map((h, i) => (
                <div key={i} style={s.trendCard}>
                  <div style={{ ...s.trendIcon, background: tone(h.color, '22'), color: h.color }}>
                    {h.icon}
                  </div>
                  <div>
                    <div style={{ ...s.trendTitle, color: h.color }}>{h.title}</div>
                    <div style={s.trendDesc}>{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...s.exampleBox, marginTop: 24 }}>
              <div style={s.exampleTitle}>たとえば</div>
              <div style={s.exampleBody}>
                該当者が0人なのか、そもそも判定に必要なデータが無いのか。
                これを同じ「0」で表示すると、判断を誤ります。AIペルソナは
                <strong style={{ color: 'rgba(255,255,255,0.85)' }}>「判定不能」と「該当0人」を別のものとして扱います。</strong>
              </div>
            </div>
          </div>
        )}

        {/* ─ 09 AI状況示唆 ─ */}
        {activeSlide === 8 && (
          <div>
            <h3 style={s.subHeading}>「分けたから見えたこと」を、AIが言葉にします</h3>
            <p style={s.subDesc}>
              集計はすべて機械が行い、AIは解釈だけを担当します。AIに数字を作らせないため、
              出力に含まれる数値が集計結果に実在するかを機械で検証しています。
            </p>

            <div style={s.questionGrid}>
              {INSIGHT_QUESTIONS.map((q, i) => (
                <div key={i} style={{ ...s.questionCard, borderColor: tone(q.color, '33') }}>
                  <div style={{ ...s.questionLabel, color: q.color }}>{q.q}</div>
                  <div style={s.questionDesc}>{q.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ ...s.exampleBox, marginTop: 24 }}>
              <div style={s.exampleTitle}>実際に出た示唆（デモ環境）</div>
              <div style={s.exampleBody}>
                「ブランドロイヤル層は最頻ブランドの購入比率が83.5%（全体49.4%）と突出して高いにもかかわらず、
                顧客単価5,872円は全体9,353円を下回り、全ペルソナ中最も低い」
                <br />
                <br />
                「PU-01はLV-03に完全に包含されている（重複比率1.0）。別ペルソナとして維持する意義を確認すべき」
              </div>
            </div>

            <p style={s.footnote}>
              ※ 実行するたびに1世代として保存し、10世代まで履歴を残します。
              前回の示唆も踏まえて「前回言ったことがどうなったか」まで書きます。
            </p>
          </div>
        )}

        {/* ─ 10 導入効果 ─ */}
        {activeSlide === 9 && (
          <div>
            <h3 style={s.subHeading}>Before / After</h3>
            <div style={s.baTable}>
              <div style={s.baHead}>
                <div style={s.baHeadCell}>観点</div>
                <div style={{ ...s.baHeadCell, color: 'rgba(248,113,113,0.9)' }}>導入前</div>
                <div style={{ ...s.baHeadCell, color: '#34d399' }}>導入後</div>
              </div>
              {BEFORE_AFTER.map((b, i) => (
                <div key={i} style={s.baRow}>
                  <div style={s.baTheme}>{b.theme}</div>
                  <div style={s.baBefore}>{b.before}</div>
                  <div style={s.baAfter}>{b.after}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ 11 導入ステップ ─ */}
        {activeSlide === 10 && (
          <div>
            <h3 style={s.subHeading}>6ステップで運用に乗ります</h3>
            <p style={s.subDesc}>
              既にSmart Hubをご利用の場合、01と02は済んでいることが多く、03から始められます。
            </p>
            <div style={s.workflowList}>
              {STEPS.map((ws, i) => (
                <div key={i} style={s.workflowItem}>
                  <div style={s.workflowLeft}>
                    <div style={{ ...s.workflowNum, background: ws.color }}>{ws.num}</div>
                    {i < STEPS.length - 1 && (
                      <div
                        style={{
                          ...s.workflowLine,
                          background: `linear-gradient(180deg, ${ws.color}, ${STEPS[i + 1].color})`
                        }}
                      />
                    )}
                  </div>
                  <div style={{ ...s.workflowContent, borderColor: tone(ws.color, '33') }}>
                    <span style={{ ...s.workflowTitle, color: ws.color }}>{ws.title}</span>
                    <p style={s.workflowDesc}>{ws.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ 12 ロードマップ ─ */}
        {activeSlide === 11 && (
          <div>
            <h3 style={s.subHeading}>開発中の次の一手</h3>
            <p style={s.subDesc}>
              いま測れているのは「買い方」までです。次は「探し方」と「出し分け先」を広げます。
            </p>
            <div style={s.geoValueGrid}>
              {ROADMAP.map((r, i) => (
                <div key={i} style={{ ...s.geoValueCard, borderColor: tone(r.color, '33') }}>
                  <div style={{ ...s.geoValueNum, color: r.color }}>{r.icon}</div>
                  <div style={{ ...s.geoValueTitle, color: r.color }}>{r.title}</div>
                  <p style={s.geoValueDesc}>{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer style={s.footer}>
        <p style={s.footerText}>AIペルソナ ─ powered by W2 Smart Hub</p>
      </footer>
    </div>
  )
}

/* ━━━ スタイル ━━━ */

const s: Record<string, CSSProperties> = {
  page: { maxWidth: 960, margin: '0 auto', padding: '0 0 40px', position: 'relative' },

  exportBar: { display: 'flex', justifyContent: 'flex-end', marginBottom: 12 },
  exportBtn: {
    padding: '8px 20px',
    borderRadius: 10,
    border: '1px solid rgba(16,185,129,0.3)',
    background: 'rgba(16,185,129,0.1)',
    color: '#6ee7b7',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  },

  hero: {
    textAlign: 'center',
    padding: '48px 32px 32px',
    background:
      'linear-gradient(160deg, rgba(16,185,129,0.10) 0%, rgba(6,182,212,0.10) 50%, rgba(245,158,11,0.06) 100%)',
    borderRadius: 24,
    border: '1px solid rgba(16,185,129,0.15)',
    marginBottom: 32
  },
  heroBadge: {
    display: 'inline-block',
    padding: '4px 18px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    background: 'rgba(16,185,129,0.15)',
    color: '#6ee7b7',
    marginBottom: 16
  },
  heroTitle: {
    fontSize: 36,
    fontWeight: 800,
    color: '#fff',
    margin: '0 0 2px',
    letterSpacing: '-0.02em'
  },
  heroAccent: {
    background: 'linear-gradient(135deg, #34d399 0%, #22d3ee 50%, #fbbf24 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  },
  heroTitleSub: {
    display: 'block',
    fontSize: '0.36em',
    fontWeight: 500,
    marginTop: 6,
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '0.08em'
  },
  heroSub: {
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.35)',
    margin: '0 0 18px',
    letterSpacing: '0.06em'
  },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, margin: '0 0 24px' },

  moduleRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
  moduleCard: {
    padding: '16px 18px',
    borderRadius: 16,
    textAlign: 'left',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid'
  },
  moduleHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  moduleTag: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    letterSpacing: '0.06em'
  },
  moduleName: { fontSize: 13, fontWeight: 700, marginBottom: 6 },
  moduleDesc: { fontSize: 11.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 },
  statCard: {
    padding: '16px 12px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  statNum: {
    fontSize: 24,
    fontWeight: 900,
    background: 'linear-gradient(135deg, #34d399, #22d3ee)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: 4
  },
  statLabel: { fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600, marginBottom: 3 },
  statSrc: { fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.4 },

  agendaSection: { marginBottom: 8 },
  agendaSectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 4px',
    textAlign: 'center'
  },
  agendaHint: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    textAlign: 'center',
    margin: '0 0 16px'
  },
  agendaGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  agendaCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)',
    cursor: 'pointer',
    textAlign: 'left'
  },
  agendaCardActive: {
    background: 'rgba(16,185,129,0.12)',
    border: '1px solid rgba(16,185,129,0.4)'
  },
  agendaNum: {
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.25)',
    flexShrink: 0,
    marginTop: 1
  },
  agendaNumActive: { color: '#34d399' },
  agendaBody: { minWidth: 0 },
  agendaLabel: { fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.65)' },
  agendaLabelActive: { color: '#fff' },
  agendaMsg: { fontSize: 10.5, color: 'rgba(255,255,255,0.32)', lineHeight: 1.45, marginTop: 2 },
  agendaMsgActive: { color: 'rgba(255,255,255,0.55)' },

  slideContainer: { marginTop: 28 },
  sectionDivider: {
    height: 1,
    background:
      'linear-gradient(90deg, transparent, rgba(16,185,129,0.3), rgba(6,182,212,0.3), transparent)',
    marginBottom: 24
  },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 },
  sectionNum: {
    fontSize: 30,
    fontWeight: 900,
    background: 'linear-gradient(135deg, #34d399, #22d3ee)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1
  },
  sectionTitle: { fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 },
  sectionSub: { fontSize: 12.5, color: 'rgba(255,255,255,0.4)', margin: '3px 0 0' },

  subHeading: { fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 8px' },
  subDesc: { fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.85, margin: '0 0 20px' },
  footnote: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    lineHeight: 1.7,
    margin: '14px 0 0'
  },

  painGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  painCard: {
    display: 'flex',
    gap: 12,
    padding: '16px 18px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)'
  },
  painDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0, marginTop: 6 },
  painProblem: { fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 5 },
  painImpact: { fontSize: 11.5, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 },

  alertBanner: {
    display: 'flex',
    gap: 12,
    padding: '16px 20px',
    borderRadius: 14,
    background: 'rgba(251,191,36,0.07)',
    border: '1px solid rgba(251,191,36,0.2)'
  },
  alertText: { fontSize: 12.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.8, margin: 0 },

  scopeGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 },
  scopeCard: { padding: '20px 22px', borderRadius: 18, border: '1px solid' },
  scopeHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  scopeBadge: { fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  scopeTitle: { fontSize: 15, fontWeight: 700, marginBottom: 12 },
  scopeList: { listStyle: 'none', padding: 0, margin: '0 0 14px' },
  scopeItem: {
    display: 'flex',
    gap: 8,
    fontSize: 12,
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.7,
    marginBottom: 6
  },
  scopeExample: {
    fontSize: 11.5,
    color: 'rgba(255,255,255,0.45)',
    lineHeight: 1.7,
    padding: '10px 14px',
    borderRadius: 10,
    background: 'rgba(0,0,0,0.2)',
    whiteSpace: 'pre-line'
  },

  axisGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 },
  axisCard: {
    padding: '14px 12px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid'
  },
  axisName: { fontSize: 12.5, fontWeight: 700, marginBottom: 5 },
  axisDesc: { fontSize: 10.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55 },

  measuredGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  measuredCard: {
    padding: '16px 18px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid'
  },
  measuredPersona: { fontSize: 12.5, fontWeight: 700, marginBottom: 4 },
  measuredMetric: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 12 },
  measuredRow: { display: 'flex', alignItems: 'center', gap: 14 },
  measuredValue: { fontSize: 26, fontWeight: 900, lineHeight: 1 },
  measuredSite: { fontSize: 20, fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1 },
  measuredArrow: { fontSize: 14, color: 'rgba(255,255,255,0.25)' },
  measuredCaption: { fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 5 },

  dualGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 },
  dualCard: {
    padding: '20px 22px',
    borderRadius: 18,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid'
  },
  dualHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  dualTitle: { fontSize: 15, fontWeight: 700 },
  dualDesc: { fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, margin: '0 0 12px' },
  dualNote: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.5)',
    padding: '8px 12px',
    borderRadius: 8,
    background: 'rgba(0,0,0,0.2)',
    border: '1px solid'
  },

  trendGrid: { display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 12 },
  trendCard: {
    display: 'flex',
    gap: 14,
    padding: '16px 18px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.07)'
  },
  trendIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0
  },
  trendTitle: { fontSize: 13.5, fontWeight: 700, marginBottom: 5 },
  trendDesc: { fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 },

  exampleBox: {
    padding: '18px 22px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  exampleTitle: { fontSize: 11, fontWeight: 700, color: '#34d399', marginBottom: 8 },
  exampleBody: { fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.85 },

  flowRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  flowStep: { textAlign: 'center', flex: 1 },
  flowIcon: {
    width: 54,
    height: 54,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    margin: '0 auto 8px'
  },
  flowLabel: { fontSize: 11.5, color: 'rgba(255,255,255,0.6)', fontWeight: 600 },
  flowArrow: { fontSize: 16, color: 'rgba(255,255,255,0.2)', flexShrink: 0 },

  funcGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  funcCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 14px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid'
  },
  funcName: { fontSize: 12, color: 'rgba(255,255,255,0.7)' },

  workflowList: { display: 'flex', flexDirection: 'column' },
  workflowItem: { display: 'flex', gap: 16 },
  workflowLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
  workflowNum: {
    width: 30,
    height: 30,
    borderRadius: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    color: '#fff'
  },
  workflowLine: { width: 2, flex: 1, minHeight: 16 },
  workflowContent: {
    flex: 1,
    padding: '14px 18px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid',
    marginBottom: 12
  },
  workflowTitle: { fontSize: 13.5, fontWeight: 700 },
  workflowDesc: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.55)',
    lineHeight: 1.75,
    margin: '5px 0 0'
  },

  questionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  questionCard: {
    padding: '16px 18px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid'
  },
  questionLabel: { fontSize: 14, fontWeight: 800, marginBottom: 6 },
  questionDesc: { fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 },

  baTable: {
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.08)'
  },
  baHead: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.6fr 1.6fr',
    background: 'rgba(255,255,255,0.05)'
  },
  baHeadCell: {
    padding: '12px 16px',
    fontSize: 11.5,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.6)'
  },
  baRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.6fr 1.6fr',
    borderTop: '1px solid rgba(255,255,255,0.06)'
  },
  baTheme: {
    padding: '14px 16px',
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.8)'
  },
  baBefore: {
    padding: '14px 16px',
    fontSize: 11.5,
    color: 'rgba(248,113,113,0.75)',
    lineHeight: 1.7
  },
  baAfter: { padding: '14px 16px', fontSize: 11.5, color: '#6ee7b7', lineHeight: 1.7 },

  geoValueGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  geoValueCard: {
    padding: '20px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid'
  },
  geoValueNum: { fontSize: 28, fontWeight: 900, marginBottom: 8 },
  geoValueTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
  geoValueDesc: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 },

  footer: {
    textAlign: 'center',
    padding: '32px 0 0',
    marginTop: 40,
    borderTop: '1px solid rgba(255,255,255,0.08)'
  },
  footerText: { fontSize: 13, color: 'rgba(255,255,255,0.3)', margin: 0 }
}
